import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {createPortal} from 'react-dom';
import styled from 'styled-components';
import {Z_INDEXES} from '../../../constants/layers';
import {useToastPortalElement} from '../../../hooks/usePortalElement/useToastPortalElement';
import {Toast, ToastTypes} from './Toast';

export type ShowToastProps = PropsWithChildren<{
  // callback triggered when toast disappears after the specified amount of ms
  onAutoClose?: VoidFunction;
  // auto close time in ms
  autoHideTimeoutMs?: number;
  autoHide?: boolean;
  // callback triggered when clicking the close button
  onDismiss?: VoidFunction;
  // hide close button within the toast
  hideCloseButton?: boolean;
  // flag that sets the toast to be full-size, from border to border when set to true
  detachedFromBorders?: boolean;
  // prop that determines the styling of the toast based on the defined types
  type: ToastTypes;
  // sets the toast display animation direction. Note: It refers only the "incoming direction" as the placement of the toast will be the opposite.
  direction?: 'up' | 'down' | 'left' | 'right';
  // offset to the positioning of the toast
  offset?: string;
  // content rendered inside the toast
  content: React.ReactElement | string;
  // used for testing purposes
  ['data-testid']?: string;
  position?: 'top left' | 'top right' | 'bottom left' | 'bottom right';
}>;

interface ToastProps extends ShowToastProps {
  uuid: number;
}

interface ProviderProps {
  maxToasts?: number;
}

export const ToastContext = createContext<{
  showToast: (toast: ShowToastProps) => void;
  removeAllToasts: VoidFunction;
}>({
  showToast: () => void 0,
  removeAllToasts: () => void 0,
});

export const ToastContextProvider: React.FC<
  React.PropsWithChildren<ProviderProps>
> = ({
  // should add prop for max number of toasts displayed
  children,
  maxToasts,
}) => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const toastPortalElement = useToastPortalElement();

  const removeToast = (toastUuid: number) => {
    setToasts(toasts.filter(toast => toast.uuid !== toastUuid));
  };

  const showToast = useCallback(
    (toast: ShowToastProps) => {
      // if the length of the currently stored toasts plus the newly added one is greater than the maxToasts we will remove the first toast from the array
      const updatedToasts =
        maxToasts && toasts.length + 1 > maxToasts ? toasts.slice(1) : toasts;

      // we need to add a uuid to each toast in order to be able to remove them properly
      const appendedToasts = [
        ...updatedToasts,
        {...toast, uuid: performance.now()},
      ];
      setToasts(appendedToasts);
    },
    [toasts, maxToasts],
  );

  const removeAllToasts = () => {
    setToasts([]);
  };

  const value = useMemo(() => ({showToast, removeAllToasts}), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toastPortalElement &&
        createPortal(
          <ToastsContainer>
            {toasts.map(toast => (
              <Toast
                {...toast}
                autoHide={toast.autoHide ?? true}
                autoHideTimeoutMs={toast.autoHideTimeoutMs ?? 5000}
                key={toast.uuid}
                isShown={true}
                onDismiss={() => {
                  toast.onDismiss?.();
                  removeToast(toast.uuid);
                }}
                onAutoClose={() => {
                  toast.onAutoClose?.();
                  removeToast(toast.uuid);
                }}
              >
                {toast.content}
              </Toast>
            ))}
          </ToastsContainer>,
          toastPortalElement,
        )}
    </ToastContext.Provider>
  );
};

const ToastsContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: ${Z_INDEXES.TADARIDA_TOAST_CONTAINER};
  display: flex;
  flex-direction: column;
`;
